import React from "react";
import { OrderItem } from "@/api/models/api-types";
import { formatPrice } from "../utils/format-price";
import { useProductDetailModalStore } from "@/features/product-detail/store/product-detail-modal-store";
import { useTranslation } from "react-i18next";

interface OrderDetailProductCardProps {
  item: OrderItem;
  currency: string;
}

export const OrderDetailProductCard: React.FC<OrderDetailProductCardProps> = ({
  item,
  currency,
}) => {
  const { t } = useTranslation();
  const { openModal } = useProductDetailModalStore();

  // Get primary image
  const primaryImage =
    item.images?.find((img) => img.isPrimary) || item.images?.[0];
  const imageUrl = primaryImage?.imageUrl || null;

  // Get merchant name from item (if available)
  const merchantName = null; // This should come from order data

  // Format attributes
  const attributes = [];
  if (item.axis1Name && item.a1Value) {
    attributes.push(`${item.a1Value}`);
  }
  if (item.axis2Name && item.a2Value) {
    attributes.push(item.a2Value);
  }
  const attributeText = attributes.join(" / ");

  // Handle product click - product detayı product (Mongo ID) ile aç
  const productIdForDetail =
    typeof item.product === "string"
      ? item.product
      : (item.product as { _id?: string })?._id ?? item.productId;

  const handleClick = () => {
    if (productIdForDetail) {
      openModal(productIdForDetail);
    }
  };

  return (
    <div
      onClick={handleClick}
      className="flex gap-2 md:gap-4 p-2.5 md:p-4 rounded-lg border border-border-primary dark:border-gray-700 cursor-pointer"
    >
      {/* Product Image */}
      {imageUrl && (
        <div className="relative w-20 h-20 md:w-[124px] md:h-[124px] rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={imageUrl}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
      )}

      {/* Product Details */}
      <div className="flex-1 flex flex-col justify-between min-w-0">
        <div className="flex flex-col gap-1 md:gap-2">
          {/* Merchant Name */}
          {merchantName && (
            <p className="text-xs md:text-sm font-semibold text-text-primary dark:text-text-primary">
              {merchantName}
            </p>
          )}

          {/* Product Name */}
          <p className="text-xs md:text-sm text-text-primary dark:text-text-primary line-clamp-2 break-words">
            {item.name}
          </p>

          {/* Attributes */}
          {attributeText && (
            <p className="text-xs md:text-sm text-text-tertiary dark:text-text-tertiary">
              {attributeText}
            </p>
          )}

          {/* Price */}
          <p className="text-sm md:text-base font-semibold text-text-primary dark:text-text-primary">
            {formatPrice(item.amount, currency)}
          </p>

          {/* Item Quantity */}
          <p className="text-xs md:text-sm text-text-tertiary dark:text-text-tertiary">
            {t("orders.detail.items")}: {item.quantity}
          </p>
        </div>
      </div>
    </div>
  );
};
